import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Music, Users, ArrowRight, Sparkles, Radio, Play } from "lucide-react";
import blackpinkGroupPhoto1 from "@/assets/blackpink-group-photo-1.jpg";
import blackpinkGroupPhoto2 from "@/assets/blackpink-group-photo-2.webp";
import blackpinkAlbumCover from "@/assets/blackpink-album-cover.jpg";

const StudyWithBlackpink = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study With Blackpink - BLINK Study Rooms"
        description="Study with BLACKPINK fans worldwide. Join BLINKs in virtual study rooms with BLACKPINK playlists, themed spaces, and global community for productive sessions."
        keywords="study with blackpink, blackpink study room, blink study, kpop study room, blackpink playlist study"
        canonicalUrl="/study-with-blackpink/"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Music className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">CSW STUDY TOGETHER</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Study With{" "}
              <span className="text-gradient">BLACKPINK</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Join BLINKs worldwide who study with BLACKPINK's powerful and confident energy. Stay focused and motivated as you work toward your goals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Start A Room Now <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About BLACKPINK */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-8">
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground text-center">
              Study with <span className="text-gradient">BLACKPINK</span>
            </h2>
            <Card className="p-8 sm:p-12 bg-card/50 backdrop-blur-sm border-border/50">
              <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
                <p>
                  BLACKPINK is a globally renowned South Korean girl group formed by YG Entertainment. Consisting of Jisoo, Jennie, Rosé, and Lisa, BLACKPINK has taken the world by storm with their powerful performances and chart-topping hits like "DDU-DU DDU-DU," "Kill This Love," and "How You Like That."
                </p>
                <p>
                  BLINKs find inspiration in BLACKPINK's confidence, determination, and fierce energy. Studying with BLACKPINK means channeling that same powerful energy into your work, staying focused, and achieving excellence in everything you do.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Group Photo Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-primary/10 rounded-full blur-[100px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <Card className="overflow-hidden border-border/50 hover:border-primary/30 transition-all">
                <img 
                  src={blackpinkGroupPhoto2} 
                  alt="BLACKPINK members in elegant group photo" 
                  className="w-full h-full object-cover"
                />
              </Card>
              <div className="space-y-6">
                <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                  Study with BLACKPINK's <span className="text-gradient">Power</span>
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Channel BLACKPINK's fierce determination and confidence into your study sessions. Join BLINKs worldwide who find motivation in their powerful energy and dedication to excellence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center">
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-foreground">
                Study Features for <span className="text-gradient">BLINKs</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                { icon: <Music className="w-7 h-7" />, title: "BLACKPINK Study Playlists", desc: "Power through your study sessions with curated BLACKPINK playlists featuring their most energizing and motivational tracks." },
                { icon: <Users className="w-7 h-7" />, title: "Global BLINK Community", desc: "Connect with BLINKs from across the globe in dedicated study rooms where fans support each other's academic journeys." },
                { icon: <Sparkles className="w-7 h-7" />, title: "Iconic Themed Rooms", desc: "Study in BLACKPINK-themed rooms inspired by their iconic music videos, stages, and concepts that bring their powerful aesthetic to your workspace." },
                { icon: <Radio className="w-7 h-7" />, title: "Always Available", desc: "Join BLACKPINK study sessions 24/7. Whether it's day or night, find motivation and focus with their powerful presence anytime you need it." },
              ].map((feature, index) => (
                <Card key={index} className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center text-primary mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-4">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Album Cover Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="overflow-hidden border-border/50">
              <img 
                src={blackpinkAlbumCover} 
                alt="BLACKPINK album cover with iconic styling" 
                className="w-full h-auto object-cover"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Study with <span className="text-gradient">BLACKPINK?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join BLINKs worldwide and channel BLACKPINK's powerful energy into your studies. Start achieving your goals today.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Explore CSW Rooms <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyWithBlackpink;